import React from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'react-hot-toast'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { MultiSelect } from '@/components/ui/multi-select'
import { cn } from '@/lib/utils'
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  ClipboardList,
  ListChecks,
  Loader2,
  Plus,
  X,
} from 'lucide-react'
import {
  useCreateTeamRecurringFeedback,
  useTemplates,
  useUpdateTeamRecurringFeedback,
} from '@/hooks/api-hooks'
import { ApiError } from '@/lib/api'

const steps = ['Setup', 'Questions', 'Review'] as const

type StepKey = (typeof steps)[number]

const NOW = new Date()
const DEFAULT_START_DATE = NOW.toISOString().split('T')[0]
const DEFAULT_START_TIME = '09:00'
const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

const createId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2, 10)}`

const PREDEFINED_QUESTIONS = [
  {
    id: 'impact',
    text: "Rate this teammate's overall impact on team outcomes this period.",
    type: 'rating' as const,
    required: true,
  },
  {
    id: 'quality',
    text: 'Rate the quality and accuracy of their work.',
    type: 'rating' as const,
    required: true,
  },
  {
    id: 'reliability',
    text: 'Rate their reliability and follow-through on commitments.',
    type: 'rating' as const,
    required: true,
  },
  {
    id: 'communication',
    text: 'Rate their communication clarity and timeliness.',
    type: 'rating' as const,
    required: true,
  },
  {
    id: 'wins',
    text: 'What recent wins or positive contributions stood out?',
    type: 'text' as const,
    required: false,
  },
  {
    id: 'improvement',
    text: 'Any suggestions for improvement?',
    type: 'multiple_choice' as const,
    required: false,
    options: ['More mentorship', 'Clearer communication', 'Additional training', 'Better prioritisation'],
  },
]

const scheduleOptions = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'custom', label: 'Custom (cron)' },
] as const

const questionSchema = z
  .object({
    id: z.string().optional(),
    text: z.string().min(3, 'Enter a question'),
    type: z.enum(['text', 'rating', 'multiple_choice']),
    required: z.boolean().default(true),
    options: z.array(z.string().min(1)).optional(),
  })
  .transform((question) => ({
    ...question,
    id: question.id ?? createId(),
    options:
      question.type === 'multiple_choice'
        ? Array.isArray(question.options)
          ? question.options
          : []
        : [],
  }))
  .refine(
    (question) =>
      question.type !== 'multiple_choice' || (question.options && question.options.length >= 2),
    {
      message: 'Multiple choice questions need at least two options',
      path: ['options'],
    },
  )

const formSchema = z.object({
  name: z.string().min(3, 'Provide a name for this recurring feedback'),
  description: z.string().max(1000).optional(),
  targetUserId: z.string().min(1, 'Select the teammate who will receive this feedback'),
  participantIds: z.array(z.string()).min(1, 'Choose at least one participant'),
  schedule: z
    .object({
      frequency: z.enum(['monthly', 'quarterly', 'biweekly', 'weekly', 'custom']),
      startDate: z.string().min(1, 'Select a start date'),
      startTime: z.string().min(1, 'Select a start time'),
      timezone: z.string().optional(),
      customCron: z.string().optional(),
      dueInDays: z.coerce.number().int().min(1, 'Due days must be at least 1').default(10),
    })
    .refine(
      (schedule) =>
        schedule.frequency !== 'custom' || (schedule.customCron && schedule.customCron.trim().length > 0),
      {
        message: 'Enter a cron expression for custom schedules',
        path: ['customCron'],
      },
    ),
  templateId: z.string().optional(),
  questions: z.array(questionSchema).min(1, 'Add at least one question'),
})

type FormValues = z.infer<typeof formSchema>

function buildDefaultFormValues(teamMembers: TeamMember[]): FormValues {
  const defaultTarget = teamMembers[0]?.userId ?? ''
  const defaultParticipants = teamMembers
    .filter((member) => member.userId !== defaultTarget)
    .map((member) => member.userId)

  return {
    name: '',
    description: '',
    targetUserId: defaultTarget,
    participantIds: defaultParticipants,
    schedule: {
      frequency: 'monthly',
      startDate: DEFAULT_START_DATE,
      startTime: DEFAULT_START_TIME,
      timezone: DEFAULT_TIMEZONE,
      customCron: '',
      dueInDays: 10,
    },
    templateId: undefined,
    questions: PREDEFINED_QUESTIONS.slice(0, 3).map((question) => ({
      id: createId(),
      text: question.text,
      type: question.type,
      required: question.required,
      options: question.type === 'multiple_choice' ? question.options ?? [] : [],
    })),
  }
}

function mapCycleToForm(cycle: ExistingRecurringFeedbackCycle): FormValues {
  const schedule = cycle.schedule ?? {}
  const frequency = (schedule.frequency as FormValues['schedule']['frequency']) ?? 'monthly'
  const cronExpression =
    frequency === 'custom'
      ? schedule.cronSchedule || cycle.cronSchedule || ''
      : ''

  const participantIds = cycle.participants
    .filter((participant) => participant.role !== 'recipient')
    .map((participant) => participant.userId)
    .filter(Boolean)

  return {
    name: cycle.name || '',
    description: cycle.description ?? '',
    targetUserId: cycle.targetUserId,
    participantIds,
    schedule: {
      frequency,
      startDate: schedule.startDate ?? DEFAULT_START_DATE,
      startTime: schedule.startTime ?? DEFAULT_START_TIME,
      timezone: schedule.timezone ?? DEFAULT_TIMEZONE,
      customCron: cronExpression,
      dueInDays: typeof schedule.dueInDays === 'number' ? schedule.dueInDays : 10,
    },
    templateId: isValidUuid(cycle.templateId) ? cycle.templateId : undefined,
    questions: cycle.questions.map((question) => ({
      id: question.id || createId(),
      text: question.text,
      type: question.type,
      required: question.required,
      options: question.type === 'multiple_choice' ? question.options ?? [] : [],
    })),
  }
}

type TeamMember = {
  id: string
  userId: string
  name: string
  email?: string
}

type TemplateQuestion = {
  id?: string
  text: string
  questionType?: 'text' | 'rating' | 'multiple_choice'
  type?: 'text' | 'rating' | 'multiple-choice'
  isRequired?: boolean
  required?: boolean
  options?: string[]
}

type Template = {
  id: string
  name: string
  description?: string | null
  questions?: TemplateQuestion[]
  category?: string | null
  isDefault?: boolean
}

type CycleParticipant = {
  id: string
  userId: string
  role: 'reviewer' | 'recipient'
  user?: {
    id: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
  } | null
}

export type ExistingRecurringFeedbackCycle = {
  id: string
  name: string
  description?: string | null
  targetUserId: string
  templateId?: string | null
  cronSchedule?: string | null
  schedule?: {
    frequency?: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'custom'
    startDate?: string
    startTime?: string
    timezone?: string | null
    cronSchedule?: string | null
    nextRunDate?: string | null
    dueInDays?: number | null
  } | null
  participants: CycleParticipant[]
  questions: Array<{
    id: string
    text: string
    type: 'text' | 'rating' | 'multiple_choice'
    required: boolean
    options?: string[]
  }>
}

interface CreateRecurringFeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  teamId: string
  teamMembers: TeamMember[]
  onSuccess?: () => void
  mode?: 'create' | 'edit'
  initialCycle?: ExistingRecurringFeedbackCycle
}

function computeCronExpression(schedule: FormValues['schedule']) {
  const [rawHours, rawMinutes] = schedule.startTime.split(':')
  const hours = Number.parseInt(rawHours, 10)
  const minutes = Number.parseInt(rawMinutes, 10)
  const safeHour = Number.isNaN(hours) ? 9 : hours
  const safeMinute = Number.isNaN(minutes) ? 0 : minutes
  const date = new Date(`${schedule.startDate}T00:00:00Z`)
  const dayOfMonth = Number.isNaN(date.getUTCDate()) ? 1 : date.getUTCDate()
  const dayOfWeek = Number.isNaN(date.getUTCDay()) ? 1 : date.getUTCDay()

  switch (schedule.frequency) {
    case 'monthly':
      return `${safeMinute} ${safeHour} ${dayOfMonth} * *`
    case 'quarterly':
      return `${safeMinute} ${safeHour} ${dayOfMonth} */3 *`
    case 'biweekly':
      return `${safeMinute} ${safeHour} */14 * *`
    case 'weekly':
      return `${safeMinute} ${safeHour} * * ${dayOfWeek}`
    case 'custom':
      return schedule.customCron?.trim() ?? ''
    default:
      return ''
  }
}

function formatDisplayDate(date: string | null | undefined, time: string | null | undefined) {
  if (!date) return 'Not set'
  try {
    const iso = time ? `${date}T${time}:00` : `${date}T00:00:00`
    const parsed = new Date(iso)
    return parsed.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: time ? 'short' : undefined,
    })
  } catch (error) {
    return date
  }
}

const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/

const isValidUuid = (value?: string | null): value is string => {
  if (!value) return false
  return UUID_REGEX.test(value)
}

const normalizeQuestionType = (
  questionType?: TemplateQuestion['questionType'],
  legacyType?: TemplateQuestion['type'],
): 'text' | 'rating' | 'multiple_choice' => {
  if (questionType) return questionType
  if (legacyType === 'multiple-choice') return 'multiple_choice'
  if (legacyType === 'rating') return 'rating'
  return 'text'
}

function mapTemplateQuestion(question: TemplateQuestion) {
  const normalizedType = normalizeQuestionType(question.questionType, question.type)
  const isRequired = question.isRequired ?? question.required ?? true
  const options = normalizedType === 'multiple_choice' ? question.options ?? [] : []

  return {
    id: question.id ?? createId(),
    text: question.text,
    type: normalizedType,
    required: isRequired,
    options,
  }
}

export function CreateRecurringFeedbackModal({
  isOpen,
  onClose,
  teamId,
  teamMembers,
  onSuccess,
  mode = 'create',
  initialCycle,
}: CreateRecurringFeedbackModalProps) {
  const isEditMode = mode === 'edit' && !!initialCycle
  const [step, setStep] = React.useState<StepKey>('Setup')
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | null>(
    isEditMode ? initialCycle?.templateId ?? null : null,
  )

const defaultValues = React.useMemo(() => buildDefaultFormValues(teamMembers), [teamMembers])

  const targetOptions = React.useMemo(
    () =>
      teamMembers.map((member) => ({
        value: member.userId,
        label: member.name,
        email: member.email,
      })),
    [teamMembers],
  )

  const participantOptions = React.useMemo(
    () =>
      teamMembers.map((member) => ({
        value: member.userId,
        label: member.name,
        email: member.email,
      })),
    [teamMembers],
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const {
    fields: questionFields,
    append,
    remove,
    move,
    replace,
  } = useFieldArray({
    control: form.control,
    name: 'questions',
  })

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    if (isEditMode && initialCycle) {
      const values = mapCycleToForm(initialCycle)
      form.reset(values)
      const templateId = initialCycle.templateId ?? null
      setSelectedTemplateId(templateId)
      form.setValue('templateId', isValidUuid(templateId) ? templateId : undefined)
    } else {
      form.reset(defaultValues)
      setSelectedTemplateId(null)
      form.setValue('templateId', undefined)
    }
    setStep('Setup')
  }, [isOpen, isEditMode, initialCycle, form, defaultValues])

  const createRecurringFeedback = useCreateTeamRecurringFeedback()
  const updateRecurringFeedback = useUpdateTeamRecurringFeedback()

  const watchedQuestions = form.watch('questions')
  const watchedTargetId = form.watch('targetUserId')
  const watchedParticipants = form.watch('participantIds')
  const watchedSchedule = form.watch('schedule')

  const isSubmitting = createRecurringFeedback.isPending || updateRecurringFeedback.isPending

  const dialogTitle = isEditMode ? 'Edit Recurring Feedback' : 'Create Recurring Feedback'
  const dialogDescription = isEditMode
    ? 'Update schedule, participants, and questions for this recurring feedback cycle.'
    : 'Set up automated feedback collection for a teammate and keep a consistent pulse on their growth.'

  const {
    data: templatesResponse,
    isLoading: isLoadingTemplates,
  } = useTemplates({ type: 'feedback', includeDefaults: true })

  const templates: Template[] = React.useMemo(() => {
    const source = templatesResponse?.data ?? []
    return source.map((template) => ({
      id: template.id,
      name: template.name,
      description: template.description,
      category: template.category,
      isDefault: template.isDefault,
      questions: template.questions?.map((question) => {
        const normalizedType = normalizeQuestionType(undefined, question.type as TemplateQuestion['type'])
        return {
          id: question.id,
          text: question.text,
          questionType: normalizedType,
          type: question.type as TemplateQuestion['type'],
          isRequired: question.required,
          required: question.required,
          options: normalizedType === 'multiple_choice' ? question.options ?? [] : [],
        }
      }),
    }))
  }, [templatesResponse])

  React.useEffect(() => {
    if (watchedTargetId && watchedParticipants.includes(watchedTargetId)) {
      form.setValue(
        'participantIds',
        watchedParticipants.filter((participantId) => participantId !== watchedTargetId),
        { shouldDirty: true },
      )
    }
  }, [watchedTargetId, watchedParticipants, form])

  const handleClose = React.useCallback(() => {
    if (isEditMode && initialCycle) {
      form.reset(mapCycleToForm(initialCycle))
      setSelectedTemplateId(initialCycle.templateId ?? null)
    } else {
      form.reset(defaultValues)
      setSelectedTemplateId(null)
    }
    setStep('Setup')
    onClose()
  }, [form, onClose, isEditMode, initialCycle, defaultValues])

  const goToStep = async (direction: 'next' | 'previous') => {
    const stepIndex = steps.indexOf(step)
    if (direction === 'next') {
      const sectionsToValidate: (keyof FormValues)[] =
        step === 'Setup' ? ['name', 'targetUserId', 'participantIds', 'schedule'] : step === 'Questions' ? ['questions'] : []

      if (sectionsToValidate.length > 0) {
        const valid = await form.trigger(sectionsToValidate, { shouldFocus: true })
        if (!valid) {
          return
        }
      }

      setStep(steps[Math.min(stepIndex + 1, steps.length - 1)])
    } else {
      setStep(steps[Math.max(stepIndex - 1, 0)])
    }
  }

  const surfaceApiValidationErrors = (
    error: unknown,
    fallbackMessage: string,
    targetStep: StepKey = 'Setup',
  ) => {
    let friendlyMessage = fallbackMessage
    let firstFieldKey: string | null = null

    const tryParsePayload = (message: string | undefined) => {
      if (!message) return null
      try {
        return JSON.parse(message)
      } catch (parseError) {
        return null
      }
    }

    const payload =
      error instanceof ApiError
        ? tryParsePayload(error.message)
        : error instanceof Error
          ? tryParsePayload(error.message)
          : null

    const fieldErrors = payload?.details?.fieldErrors as Record<string, string[] | string> | undefined
    const formErrors = Array.isArray(payload?.details?.formErrors)
      ? payload?.details?.formErrors.filter((entry: unknown) => typeof entry === 'string' && entry.length > 0)
      : []

    if (fieldErrors && typeof fieldErrors === 'object') {
      Object.entries(fieldErrors).forEach(([rawKey, value]) => {
        const messages = Array.isArray(value) ? value : [value]
        const message = messages.find((item) => typeof item === 'string' && item.length > 0) || ''
        if (!message) return

        const normalizedKey = rawKey.replace(/\[(\d+)\]/g, '.$1')

        if (!firstFieldKey) {
          firstFieldKey = normalizedKey
          friendlyMessage = message
        }

        try {
          form.setError(normalizedKey as any, {
            type: 'server',
            message,
          })
        } catch (setErrorException) {
          console.warn('Unable to set form error for key', normalizedKey, setErrorException)
        }
      })
    }

    if (payload?.error && typeof payload.error === 'string') {
      friendlyMessage = payload.error
    } else if (formErrors.length > 0) {
      friendlyMessage = formErrors[0]
    }

    const key: string = firstFieldKey ?? ''

    if (key.length > 0) {
      const isQuestionError = key.startsWith('questions') || key === 'templateId'
      setStep(isQuestionError ? 'Questions' : targetStep)
      try {
        form.setFocus(key as any)
      } catch (focusError) {
        // ignore focus issues
      }
    } else if (targetStep) {
      setStep(targetStep)
    }

    toast.error(friendlyMessage)
  }

  const handleSelectTemplate = (template: Template) => {
    if (!template.questions || template.questions.length === 0) {
      toast.error('This template does not include any questions yet')
      return
    }

    setSelectedTemplateId(template.id)
    const templateId = isValidUuid(template.id) ? template.id : undefined
    form.setValue('templateId', templateId)
    replace(template.questions.map(mapTemplateQuestion))
    toast.success('Template questions added')
  }

  const handleClearTemplate = () => {
    setSelectedTemplateId(null)
    form.setValue('templateId', undefined)
  }

  const togglePredefinedQuestion = (questionId: string) => {
    const question = PREDEFINED_QUESTIONS.find((item) => item.id === questionId)
    if (!question) return

    const existingIndex = form.getValues('questions').findIndex(
      (item) => item.text.trim().toLowerCase() === question.text.trim().toLowerCase(),
    )

    if (existingIndex >= 0) {
      remove(existingIndex)
    } else {
      append({
        id: createId(),
        text: question.text,
        type: question.type,
        required: question.required,
        options: question.type === 'multiple_choice' ? question.options ?? [] : [],
      })
    }
  }

  const handleAddCustomQuestion = () => {
    append({
      id: createId(),
      text: '',
      type: 'text',
      required: true,
      options: [],
    })
  }

  const handleAddParticipantGroup = () => {
    const targetId = form.getValues('targetUserId')
    const ids = teamMembers
      .map((member) => member.userId)
      .filter((userId) => userId !== targetId)

    form.setValue('participantIds', ids, { shouldDirty: true, shouldValidate: true })
  }

  const onSubmit = form.handleSubmit((values) => {
    const timezone = values.schedule.timezone || DEFAULT_TIMEZONE
    const cronSchedule = computeCronExpression(values.schedule)

    if (!cronSchedule) {
      toast.error('Unable to derive a schedule. Please review the schedule settings.')
      setStep('Setup')
      return
    }

    const uniqueParticipantIds = Array.from(new Set(values.participantIds))

    const payload = {
      name: values.name,
      description: values.description,
      targetUserId: values.targetUserId,
      participantIds: uniqueParticipantIds,
      schedule: {
        frequency: values.schedule.frequency,
        startDate: values.schedule.startDate,
        startTime: values.schedule.startTime,
        timezone,
        cronSchedule,
        dueInDays: values.schedule.dueInDays,
      },
      templateId: values.templateId,
      questions: values.questions.map((question) => ({
        text: question.text,
        type: question.type,
        required: question.required,
        options: question.type === 'multiple_choice' ? question.options ?? [] : undefined,
      })),
    }

    if (isEditMode && initialCycle) {
      updateRecurringFeedback.mutate(
        {
          teamId,
          cycleId: initialCycle.id,
          ...payload,
        },
        {
          onSuccess: () => {
            toast.success('Recurring feedback updated')
            onSuccess?.()
            handleClose()
          },
          onError: (error: any) => {
            surfaceApiValidationErrors(error, 'Failed to update recurring feedback')
          },
        },
      )
    } else {
      createRecurringFeedback.mutate(
        {
          teamId,
          ...payload,
        },
        {
          onSuccess: () => {
            toast.success('Recurring feedback created')
            onSuccess?.()
            handleClose()
          },
          onError: (error: any) => {
            surfaceApiValidationErrors(error, 'Failed to create recurring feedback')
          },
        },
      )
    }
  })

  const stepIndex = steps.indexOf(step)

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto border border-border bg-background">
        <DialogHeader className="space-y-2">
          <DialogTitle className="flex items-center gap-3 text-2xl font-semibold">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <RefreshCwIcon className="h-5 w-5" />
            </div>
            {dialogTitle}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{dialogDescription}</p>
          <div className="mt-2 flex flex-col gap-3 md:flex-row">
            {steps.map((label, index) => (
              <div key={label} className="flex items-center gap-2 md:flex-1">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold',
                    index <= stepIndex
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground',
                  )}
                >
                  {index + 1}
                </div>
                <span
                  className={cn(
                    'text-sm font-medium',
                    index === stepIndex ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {label}
                </span>
                {index < steps.length - 1 && <div className="hidden flex-1 border-t border-dashed border-border md:block" />}
              </div>
            ))}
          </div>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-8">
            {step === 'Setup' && (
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Recurring Feedback Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g. Monthly Product Feedback" className="h-11" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="targetUserId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Target Team Member</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="h-11">
                              <SelectValue placeholder="Select team member" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {targetOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex flex-col text-left">
                                  <span className="font-medium">{option.label}</span>
                                  {option.email && (
                                    <span className="text-xs text-muted-foreground">{option.email}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <FormLabel className="text-sm font-medium">Participants</FormLabel>
                    <FormField
                      control={form.control}
                      name="participantIds"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <MultiSelect
                              options={participantOptions.map((option) => ({ value: option.value, label: option.label }))}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select participants"
                              className="h-auto min-h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={handleAddParticipantGroup}
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add entire team
                      </Button>
                      <span>Target teammate is excluded automatically.</span>
                    </div>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={3}
                          placeholder="Describe the purpose of this recurring feedback cycle"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="schedule.frequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Schedule</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="h-11">
                              <SelectValue placeholder="Select schedule" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {scheduleOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="schedule.timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Timezone</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Timezone" className="h-11" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="schedule.startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Start Date</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input {...field} type="date" min={DEFAULT_START_DATE} className="h-11" />
                            <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="schedule.startTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Start Time</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input {...field} type="time" className="h-11" />
                            <Clock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="schedule.dueInDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Responses due (days after send)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          {...field}
                          value={field.value ?? 10}
                          onChange={(event) => field.onChange(Number(event.target.value))}
                          className="h-11"
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">Defaults to 10 days. Enter how many days respondents have to submit.</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {watchedSchedule.frequency === 'custom' && (
                  <FormField
                    control={form.control}
                    name="schedule.customCron"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Cron Expression</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g. 0 9 1 * *"
                            className="h-11"
                          />
                        </FormControl>
                        <FormMessage />
                        <p className="text-xs text-muted-foreground">
                          Provide a five-field cron schedule to control when this feedback repeats.
                        </p>
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}

            {step === 'Questions' && (
              <div className="space-y-6">
                <div className="rounded-xl border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">Quick Templates</h3>
                      <p className="text-sm text-muted-foreground">
                        Start with a template and fine-tune questions for this teammate.
                      </p>
                    </div>
                    {selectedTemplateId && (
                      <Button type="button" variant="ghost" size="sm" onClick={handleClearTemplate}>
                        <X className="mr-1 h-3 w-3" /> Clear template
                      </Button>
                    )}
                  </div>
                  <div className="mt-4 grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                    {isLoadingTemplates ? (
                      <div className="col-span-full flex items-center gap-3 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading templates...
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="col-span-full rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                        No feedback templates found yet.
                      </div>
                    ) : (
                      templates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => handleSelectTemplate(template)}
                          className={cn(
                            'w-full rounded-xl border p-4 text-left transition hover:border-primary/70 hover:shadow-sm',
                            selectedTemplateId === template.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border bg-background',
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <ClipboardList className="h-4 w-4 text-primary" />
                              <div>
                                <h4 className="text-sm font-semibold text-foreground">{template.name}</h4>
                                {template.category && (
                                  <Badge variant="secondary" className="mt-1">
                                    {template.category}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                          {template.description && (
                            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                              {template.description}
                            </p>
                          )}
                          <div className="mt-3 text-xs text-muted-foreground">
                            {template.questions?.length ?? 0} question{(template.questions?.length ?? 0) === 1 ? '' : 's'}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">Pre-defined Questions</h3>
                      <p className="text-sm text-muted-foreground">
                        Toggle questions to add or remove them from this feedback set.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        PREDEFINED_QUESTIONS.forEach((question) => {
                          const exists = form
                            .getValues('questions')
                            .some((item) => item.text.trim().toLowerCase() === question.text.trim().toLowerCase())
                          if (!exists) {
                            append({
                              id: createId(),
                              text: question.text,
                              type: question.type,
                              required: question.required,
                              options: question.type === 'multiple_choice' ? question.options ?? [] : [],
                            })
                          }
                        })
                      }}
                    >
                      <Plus className="mr-1 h-3 w-3" /> Add all
                    </Button>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {PREDEFINED_QUESTIONS.map((question) => {
                      const isActive = form
                        .getValues('questions')
                        .some((item) => item.text.trim().toLowerCase() === question.text.trim().toLowerCase())

                      return (
                        <button
                          key={question.id}
                          type="button"
                          onClick={() => togglePredefinedQuestion(question.id)}
                          className={cn(
                            'rounded-xl border p-4 text-left transition hover:border-primary/70 hover:shadow-sm',
                            isActive ? 'border-primary bg-primary/5' : 'border-border bg-background',
                          )}
                        >
                          <div className="flex items-center justify-between text-sm font-medium text-foreground">
                            {question.text}
                            {isActive ? (
                              <Check className="h-4 w-4 text-primary" />
                            ) : (
                              <Plus className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="capitalize">
                              {question.type.replace('_', ' ')}
                            </Badge>
                            <Badge variant={question.required ? 'default' : 'secondary'}>
                              {question.required ? 'Required' : 'Optional'}
                            </Badge>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-foreground">Question Builder</h3>
                    <Button type="button" size="sm" onClick={handleAddCustomQuestion}>
                      <Plus className="mr-1 h-3 w-3" /> Add question
                    </Button>
                  </div>

                  {questionFields.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                      Add your first question using the button above or by selecting a template.
                    </div>
                  ) : (
                    questionFields.map((field, index) => {
                      const questionType = watchedQuestions[index]?.type ?? 'text'
                      const questionOptions = watchedQuestions[index]?.options ?? []

                      return (
                        <div
                          key={field.id}
                          className="rounded-xl border border-border bg-background p-5 shadow-sm"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                                {index + 1}
                              </span>
                              Question
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8"
                                onClick={() => move(index, index - 1)}
                                disabled={index === 0}
                              >
                                <ChevronUpIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8"
                                onClick={() => move(index, index + 1)}
                                disabled={index === questionFields.length - 1}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 text-destructive"
                                onClick={() => remove(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="mt-4 space-y-4">
                            <FormField
                              control={form.control}
                              name={`questions.${index}.text`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm font-medium">Question text</FormLabel>
                                  <FormControl>
                                    <Input {...field} placeholder="Enter question" className="h-11" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <div className="grid gap-4 md:grid-cols-3">
                              <FormField
                                control={form.control}
                                name={`questions.${index}.type`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-sm font-medium">Response type</FormLabel>
                                    <Select value={field.value} onValueChange={field.onChange}>
                                      <FormControl>
                                        <SelectTrigger className="h-11">
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="text">Text response</SelectItem>
                                        <SelectItem value="rating">Rating (1-10)</SelectItem>
                                        <SelectItem value="multiple_choice">Multiple choice</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name={`questions.${index}.required`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-sm font-medium">Required</FormLabel>
                                    <div className="flex h-11 items-center rounded-md border border-border px-3">
                                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                                      <span className="ml-2 text-sm text-muted-foreground">
                                        {field.value ? 'Required' : 'Optional'}
                                      </span>
                                    </div>
                                  </FormItem>
                                )}
                              />

                              <div className="flex items-end text-xs text-muted-foreground">
                                <ListChecks className="mr-2 h-4 w-4" />
                                {questionType === 'multiple_choice'
                                  ? 'Provide at least two options for responders to choose from.'
                                  : questionType === 'rating'
                                  ? 'Responders provide a numeric rating from 1 to 10.'
                                  : 'Responders can provide a free-form response.'}
                              </div>
                            </div>

                            {questionType === 'multiple_choice' && (
                              <div className="space-y-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-primary">Multiple choice options</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      form.setValue(
                                        `questions.${index}.options`,
                                        [...questionOptions, ''],
                                        { shouldDirty: true },
                                      )
                                    }
                                  >
                                    <Plus className="mr-1 h-3 w-3" /> Add option
                                  </Button>
                                </div>
                                {questionOptions.length === 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    Add at least two options for respondents to select from.
                                  </p>
                                )}
                                {questionOptions.map((option, optionIndex) => (
                                  <div key={`${field.id}-option-${optionIndex}`} className="flex items-center gap-3">
                                    <Input
                                      value={option}
                                      onChange={(event) => {
                                        const updated = [...questionOptions]
                                        updated[optionIndex] = event.target.value
                                        form.setValue(`questions.${index}.options`, updated, { shouldDirty: true })
                                      }}
                                      placeholder={`Option ${optionIndex + 1}`}
                                      className="h-10"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive"
                                      onClick={() => {
                                        const updated = questionOptions.filter((_, idx) => idx !== optionIndex)
                                        form.setValue(`questions.${index}.options`, updated, { shouldDirty: true })
                                      }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )}

            {step === 'Review' && (
              <div className="space-y-6">
                <div className="rounded-xl border border-border bg-muted/30 p-5">
                  <h3 className="text-base font-semibold text-foreground">Overview</h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <span className="text-xs uppercase text-muted-foreground">Feedback name</span>
                      <p className="text-sm font-medium text-foreground">{form.getValues('name')}</p>
                    </div>
                    <div>
                      <span className="text-xs uppercase text-muted-foreground">Target teammate</span>
                      <p className="text-sm font-medium text-foreground">
                        {targetOptions.find((option) => option.value === form.getValues('targetUserId'))?.label || '—'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs uppercase text-muted-foreground">Schedule</span>
                      <p className="text-sm font-medium text-foreground">
                        {scheduleOptions.find((option) => option.value === watchedSchedule.frequency)?.label || 'Not set'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Starts {formatDisplayDate(watchedSchedule.startDate, watchedSchedule.startTime)} ({watchedSchedule.timezone || DEFAULT_TIMEZONE})
                      </p>
                    </div>
                    <div>
                      <span className="text-xs uppercase text-muted-foreground">Participants</span>
                      <p className="text-sm font-medium text-foreground">
                        {form.getValues('participantIds').length} participant
                        {form.getValues('participantIds').length === 1 ? '' : 's'}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {form.getValues('participantIds').map((participantId) => {
                          const participant = participantOptions.find((option) => option.value === participantId)
                          return participant ? (
                            <Badge key={participantId} variant="secondary" className="capitalize">
                              {participant.label}
                            </Badge>
                          ) : null
                        })}
                      </div>
                    </div>
                  </div>
                  {form.getValues('description') && (
                    <div className="mt-4">
                      <span className="text-xs uppercase text-muted-foreground">Description</span>
                      <p className="text-sm text-foreground">{form.getValues('description')}</p>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-border p-5">
                  <h3 className="text-base font-semibold text-foreground">
                    Questions ({form.getValues('questions').length})
                  </h3>
                  <div className="mt-3 space-y-3">
                    {form.getValues('questions').map((question, index) => (
                      <div key={question.id} className="rounded-lg border border-dashed border-border/70 bg-background p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-muted-foreground">Q{index + 1}</span>
                              <Badge variant="outline" className="capitalize">
                                {question.type.replace('_', ' ')}
                              </Badge>
                              <Badge variant={question.required ? 'default' : 'secondary'}>
                                {question.required ? 'Required' : 'Optional'}
                              </Badge>
                            </div>
                            <p className="mt-2 text-sm font-medium text-foreground">{question.text}</p>
                            {question.type === 'multiple_choice' && question.options && (
                              <p className="mt-2 text-xs text-muted-foreground">
                                Options: {question.options.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-border pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => goToStep('previous')}
                disabled={step === 'Setup' || isSubmitting}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>

                {step !== 'Review' ? (
                  <Button
                    type="button"
                    onClick={() => goToStep('next')}
                    disabled={isSubmitting}
                  >
                    Continue <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isEditMode ? 'Save changes' : 'Create recurring feedback'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function RefreshCwIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5v4l3-3"></path>
      <path d="M21 12a9 9 0 1 1-3-6.7L21 9"></path>
    </svg>
  )
}

function ChevronUpIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m18 15-6-6-6 6" />
    </svg>
  )
}
